import { css } from '@emotion/react';

import generateEmotionClasses from '@core/utils/generateEmotionClasses';

export const classes = generateEmotionClasses('form-card', [
  'label',
  'success',
  'error',
  'disabled',
  'checked',
  'pressed',
  'input',
  'focusVisible',
  'hasIcon',
  'icon',
  'labelText',
  'labelSuffix',
  'supportText',
  'nonInteractiveSpace',
  'isCheckbox',
  'isRadio',
]);

const getFormCardCss = css`
  --label-icon-size: 20px;
  --label-radio-size: 20px;
  --label-checkbox-size: 18px;
  --label-input-size: 24px;

  &.${classes.label} {
    position: relative;
    display: grid;
    grid-template-areas:
      'input label'
      '. supportText'
      '. nonInteractiveSpace';
    grid-template-columns:
      calc(var(--label-input-size) + var(--cds-spacing-100))
      auto;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    background-color: var(--cds-color-neutral-background-primary);
    padding: var(--cds-spacing-100);
    cursor: pointer;

    &.${classes.focusVisible}::after {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      border-radius: var(--cds-border-radius-100);
      box-shadow: 0 0 0 1px var(--cds-color-interactive-stroke-primary-focus),
        0 0 0 2px var(--cds-color-interactive-background-primary-hover-weak);
    }

    &.${classes.hasIcon} {
      grid-template-areas:
        'input icon label'
        '. . supportText'
        '. nonInteractiveSpace nonInteractiveSpace';

      .${classes.input} {
        padding-inline-end: var(--cds-spacing-50);
      }
    }

    &.${classes.isRadio} {
      grid-template-columns:
        calc(var(--label-radio-size) + var(--cds-spacing-100))
        auto;

      .${classes.input} {
        padding-inline-end: var(--cds-spacing-100);
      }

      &.${classes.hasIcon} {
        grid-template-columns:
          calc(var(--label-radio-size) + var(--cds-spacing-50)) calc(
            var(--label-icon-size) + var(--cds-spacing-100)
          )
          auto;

        .${classes.input} {
          padding-inline-end: var(--cds-spacing-50);
        }
      }
    }

    &.${classes.isCheckbox} {
      grid-template-columns:
        calc(var(--label-checkbox-size) + var(--cds-spacing-100))
        auto;

      .${classes.input} {
        padding-top: 1px;
        padding-bottom: 1px;
        padding-inline-end: calc(var(--cds-spacing-100));
      }

      &.${classes.hasIcon} {
        grid-template-columns:
          calc(var(--label-checkbox-size) + var(--cds-spacing-50)) calc(
            var(--label-icon-size) + var(--cds-spacing-100)
          )
          auto;
        .${classes.input} {
          padding-inline-end: var(--cds-spacing-50);
        }
      }
    }

    .${classes.icon} {
      grid-area: icon;
      margin-top: 2px;
      color: var(--cds-color-neutral-primary);

      > svg {
        display: block;
      }
    }

    .${classes.input} {
      grid-area: input;
      align-self: start;
      padding: 0;
      color: var(--cds-color-neutral-stroke-primary);
      fill: var(--cds-color-neutral-primary-invert);
      border-radius: var(--cds-border-radius-100);
      margin-inline-end: 0;
      padding-inline-end: 0;

      & input + svg {
        fill: inherit;
      }
    }

    .${classes.labelText} {
      display: flex;
      grid-area: label;
      word-break: break-word;
      color: var(--cds-color-neutral-primary);

      .${classes.labelSuffix} {
        margin-left: auto;
        padding-left: var(--cds-spacing-100);
        white-space: nowrap;
      }
    }

    .${classes.supportText} {
      grid-area: supportText;
      color: var(--cds-color-neutral-primary-weak);
    }

    .${classes.nonInteractiveSpace} {
      grid-area: nonInteractiveSpace;
      margin-top: var(--cds-spacing-200);
    }

    &.${classes.checked} {
      border-color: var(--cds-color-interactive-primary);

      .${classes.input}, .${classes.labelText} {
        color: var(--cds-color-interactive-primary);
        fill: transparent;
      }
    }

    &:hover,
    &.${classes.focusVisible} {
      background-color: var(
        --cds-color-interactive-background-primary-hover-weak
      );
      border-color: var(--cds-color-interactive-primary);

      .${classes.input}, .${classes.labelText} {
        color: var(--cds-color-interactive-primary-hover);
      }
    }

    &.${classes.success}:not(.${classes.disabled}) {
      background-color: var(--cds-color-feedback-background-success-weak);
      border-color: var(--cds-color-feedback-success);

      .${classes.input}, .${classes.labelText} {
        color: var(--cds-color-feedback-success);
      }

      &:hover,
      &.${classes.focusVisible} {
        border-color: var(--cds-color-feedback-success-hover);

        .${classes.input}, .${classes.labelText} {
          color: var(--cds-color-feedback-success-hover);
        }
      }
    }

    &.${classes.error}:not(.${classes.disabled}) {
      background-color: var(--cds-color-feedback-background-error-weak);
      border-color: var(--cds-color-feedback-error);

      .${classes.input}, .${classes.labelText} {
        color: var(--cds-color-feedback-error);
      }

      &:hover,
      &.${classes.focusVisible} {
        border-color: var(--cds-color-feedback-error-hover);

        .${classes.input}, .${classes.labelText} {
          color: var(--cds-color-feedback-error-hover);
        }
      }
    }

    &.${classes.pressed} {
      background-color: var(
        --cds-color-interactive-background-primary-pressed-weak
      );
      border-color: var(--cds-color-interactive-primary-pressed);

      .${classes.input}, .${classes.labelText} {
        color: var(--cds-color-interactive-primary-pressed);
      }
    }

    &.${classes.disabled} {
      cursor: default;
      border-color: var(--cds-color-neutral-disabled-strong);
      background-color: var(--cds-color-neutral-background-primary-weak);

      .${classes.input}, .${classes.labelText}, .${classes.supportText} {
        color: var(--cds-color-neutral-disabled-strong);
        fill: var(--cds-color-neutral-disabled);
      }

      &.${classes.checked} {
        .${classes.input} {
          fill: var(--cds-color-neutral-disabled-strong);
        }
      }
    }
  }
`;

export default getFormCardCss;
